import { useQuery } from "@tanstack/react-query";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { motion } from "framer-motion";
import { UsersRound, Linkedin, Twitter, Sparkles } from "lucide-react";
import { useCms } from "@/hooks/useCms";
import { db } from "@/lib/firebase";
import { collection, query, orderBy, getDocs } from "firebase/firestore";

type TeamMember = {
  id: string;
  name: string;
  role: string;
  bio: string;
  image_url: string;
  linkedin_url: string;
  twitter_url: string;
  display_order: number;
  created_at: string;
};

function TeamMemberCard({ member, index }: { member: TeamMember; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      className="bg-[#0d1524] border border-white/10 rounded-3xl overflow-hidden hover:border-primary/40 transition-all duration-300 group flex flex-col h-full shadow-[0_10px_30px_-15px_rgba(0,0,0,0.3)] hover:-translate-y-1"
    >
      <div className="relative overflow-hidden aspect-[4/5] bg-slate-900 shrink-0">
        {member.image_url ? (
          <img
            src={member.image_url}
            alt={member.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/10 to-transparent flex items-center justify-center">
            <span className="text-primary font-display font-extrabold text-7xl select-none opacity-40">
              {member.name.charAt(0)}
            </span>
          </div>
        )}
        
        {/* Soft overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1524] via-transparent to-transparent opacity-80" />
        
        {/* Social Icons Float overlay on hover */}
        <div className="absolute bottom-4 right-4 flex gap-2 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-350 z-20">
          {member.linkedin_url && (
            <a
              href={member.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/95 transition-all shadow-lg hover:scale-110 active:scale-95"
              title="Connect on LinkedIn"
            >
              <Linkedin size={16} />
            </a>
          )}
          {member.twitter_url && (
            <a
              href={member.twitter_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/95 transition-all shadow-lg hover:scale-110 active:scale-95"
              title="Follow on Twitter/X"
            >
              <Twitter size={16} />
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-display font-bold text-white group-hover:text-primary transition-colors">
          {member.name}
        </h3>
        <p className="text-sm text-primary font-medium tracking-wide uppercase mt-1">
          {member.role || "Advisor"}
        </p>
        <p className="text-sm text-white/60 leading-relaxed mt-4 flex-grow">
          {member.bio}
        </p>

        {/* Social Links */}
        {(member.linkedin_url || member.twitter_url) && (
          <div className="flex items-center gap-4 mt-6 pt-4 border-t border-white/5">
            {member.linkedin_url && (
              <a
                href={member.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-primary transition-colors"
              >
                <Linkedin size={18} />
              </a>
            )}
            {member.twitter_url && (
              <a
                href={member.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-primary transition-colors"
              >
                <Twitter size={18} />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Team() {
  const { data: teamMembers = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ["/api/team"],
    queryFn: async () => {
      try {
        const q = query(collection(db, "team_members"), orderBy("display_order", "asc"));
        const snap = await getDocs(q);
        return snap.docs.map((d: any) => {
          const item = d.data();
          return {
            id: d.id,
            name: item.name || "",
            role: item.role || "",
            bio: item.bio || "",
            image_url: item.image_url || "",
            linkedin_url: item.linkedin_url || "",
            twitter_url: item.twitter_url || "",
            display_order: item.display_order || 0,
            created_at: item.created_at || "",
          };
        });
      } catch (err) {
        console.error("Failed to fetch team members from Firestore:", err);
        return [];
      }
    },
  });

  const { getMeta } = useCms();
  const heroMeta = getMeta<{ heading: string; subtext: string; badge: string }>("team_hero", {
    heading: "MEET THE TEAM",
    subtext: "We are a team of passionate sports management professionals, scouts, and advisors committed to empowering athletes, negotiating strategic deals, and steering career growth.",
    badge: "Backstage Excellence"
  });

  const sortedMembers = [...teamMembers].sort((a, b) => a.display_order - b.display_order);

  return (
    <>
      <SeoHead
        title="Our Team"
        description="Meet the exceptional staff, agents, and leaders at Eliada Sports Agency who dedicate their expertise to unlocking athletes' full potential worldwide."
        path="/team"
      />
      <PageTransition className="pt-24 pb-24 bg-[#070b13] min-h-screen">
        {/* Hero Section */}
        <div className="relative py-24 bg-secondary/30 overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent opacity-60" />
          {/* Subtle grid pattern background */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <div className="inline-flex items-center gap-1.5 text-xs text-primary font-bold uppercase tracking-wider bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20">
              <Sparkles size={12} className="animate-pulse" /> {heroMeta.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-extrabold text-white tracking-tight">
              {heroMeta.heading.split(" ").map((w, i, arr) => 
                i === arr.length - 1 ? <span key={i} className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-orange-400">{w}</span> : <span key={i}>{w} </span>
              )}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-light leading-relaxed">
              {heroMeta.subtext}
            </p>
          </div>
        </div>

        {/* Members Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          {isLoading ? (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin w-12 h-12 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : sortedMembers.length === 0 ? (
            <div className="text-center py-28 bg-[#0d1524] rounded-3xl border border-white/10 max-w-2xl mx-auto p-8">
              <UsersRound className="mx-auto text-muted-foreground/35 mb-6" size={56} />
              <h3 className="text-2xl font-display font-bold text-white mb-2">Our Team Roster</h3>
              <p className="text-muted-foreground font-light max-w-md mx-auto">
                We are currently polishing our leadership roster. Check back soon to meet the team!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {sortedMembers.map((member, i) => (
                <TeamMemberCard key={member.id} member={member} index={i} />
              ))}
            </div>
          )}
        </div>
      </PageTransition>
    </>
  );
}
